import React, { ForwardedRef, forwardRef } from 'react';
import AssetWrapper, { AssetWrapperProps } from 'src/app/atoms/Asset/AssetWrapper';
import Clickable from 'src/app/atoms/Clickable/Clickable';
import Stack from 'src/app/atoms/Layout/Stack/Stack';
import Typography, { TypographyProps } from 'src/app/atoms/Typography/Typography';
import { ImageCardContainer, ImageContainer } from './ImageCard.styles';
import Grid from 'src/app/atoms/Layout/Grid/Grid';
import Arrow from 'app/icons/Monospaced/Arrow/Arrow';
import { ColorModePropKeys } from 'styles/colors';

export interface ImageCardProps {
  href?: string;
  image: AssetWrapperProps['imageProps'];
  imageMaxWidth?: number;
  title: TypographyProps;
  supportingText?: TypographyProps;
  subHeading?: TypographyProps;
  className?: string;
  onClick?: () => void;
  imageHeight?: number;
  imageCover?: boolean;
  background?: ColorModePropKeys;
}

const ImageCard = (
  {
    href,
    image,
    title,
    supportingText,
    subHeading,
    className,
    imageMaxWidth,
    onClick,
    imageHeight,
    imageCover,
    background = 'bg-primary',
  }: ImageCardProps,
  ref: ForwardedRef<HTMLDivElement>
): JSX.Element => (
  <ImageCardContainer
    className={className}
    ref={ref}
    $clickable={!!href || !!onClick}
  >
    <Clickable
      href={href}
      onClick={onClick}
    >
      <Stack
        borderRadius='radius-2xl'
        background={background}
        padding='spacing-lg'
        direction='column'
        border='1px solid var(--border-secondary)'
        className='image-card'
      >
        <ImageContainer $imageHeight={imageHeight}>
          <AssetWrapper
            imageProps={image}
            maxWidth={imageMaxWidth}
            cover={imageCover}
          />
        </ImageContainer>
        <Stack
          gap='spacing-xs'
          direction='column'
          align='flex-start'
          justify='flex-start'
          padding={['spacing-2xl', 'spacing-xl', 'spacing-3xl']}
        >
          {subHeading && (
            <Stack
              align='flex-start'
              justify='flex-start'
              marginBottom='spacing-sm'
            >
              <Typography {...subHeading} />
            </Stack>
          )}
          <Grid>
            <Stack
              direction='row'
              gap='spacing-xl'
              justify='space-between'
              align='flex-start'
            >
              <Typography {...title} />
              {href && (
                <Arrow
                  color='var(--gray-light-400)'
                  className='arrow-icon'
                  orientation='upRight'
                  width={24}
                />
              )}
            </Stack>
          </Grid>
          {supportingText && <Typography {...supportingText} />}
        </Stack>
      </Stack>
    </Clickable>
  </ImageCardContainer>
);

export default forwardRef(ImageCard);
